import { EntityStatusChip } from '../entity-detail/EntityStatusChip.js';
import { useMemo, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  MessageSquare,
  X,
  Sparkles,
  Flame,
  AlertTriangle,
  Mail,
  ListTodo,
  CheckSquare,
} from 'lucide-react';

import { Button, Empty, Spin } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import type {
  ChatConversationItem,
  ChatContextEntityType,
} from '../../features/ai-chat/api/chat.client.js';
import { useChatConversations } from '../../features/ai-chat/hooks/useChatConversations.js';
import { useCurrentScope } from '../../shared/scope/useCurrentScope.js';

interface SystemAgentPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ConversationGroup {
  key: ChatContextEntityType;
  label: string;
  conversations: ChatConversationItem[];
}

const GROUP_ORDER: ChatContextEntityType[] = ['none', 'workspace', 'deal', 'contact'];

function groupConversations(
  conversations: ChatConversationItem[],
  getLabel: (type: ChatContextEntityType) => string,
): ConversationGroup[] {
  return GROUP_ORDER.map((type) => ({
    key: type,
    label: getLabel(type),
    conversations: conversations.filter((conversation) => conversation.contextEntityType === type),
  })).filter((group) => group.conversations.length > 0);
}

export function SystemAgentPanel({ isOpen, onClose }: SystemAgentPanelProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scope = useCurrentScope();
  const conversations = useChatConversations(
    scope,
    { contextEntityType: 'none' },
    { enabled: isOpen },
  );
  const groups = useMemo(
    () =>
      groupConversations(conversations.conversations, (type) =>
        t(`crm.system_agent.group_${type}`),
      ),
    [conversations.conversations, t],
  );

  const openChatPage = (conversationId?: string): void => {
    const query = conversationId === undefined ? '' : `?conversationId=${conversationId}`;
    navigate(`/chat${query}`);
    onClose();
  };

  const playbooks = [
    {
      id: 'pb-leads',
      label: t('crm.ai_chat.suggestions.hot_leads'),
      icon: <Flame size={13} className="text-amber-600" />,
      prompt: t('crm.ai_chat.prompt.hot_leads'),
    },
    {
      id: 'pb-risks',
      label: t('crm.ai_chat.suggestions.risk_audit'),
      icon: <AlertTriangle size={13} className="text-orange-600" />,
      prompt: t('crm.ai_chat.prompt.deal_risks'),
    },
    {
      id: 'pb-followup',
      label: t('crm.ai_chat.suggestions.follow_up'),
      icon: <Mail size={13} className="text-blue-600" />,
      prompt: t('crm.ai_chat.prompt.follow_up'),
    },
    {
      id: 'pb-tasks',
      label: t('crm.ai_chat.suggestions.daily_tasks'),
      icon: <ListTodo size={13} className="text-emerald-600" />,
      prompt: t('crm.ai_chat.prompt.daily_plan'),
    },
    {
      id: 'pb-extract-tasks',
      label: t('crm.ai_chat.suggestions.extract_tasks'),
      icon: <CheckSquare size={13} className="text-blue-600" />,
      prompt: t('crm.ai_chat.prompt.extract_tasks'),
    },
  ];

  return (
    <aside
      aria-hidden={!isOpen}
      aria-label={t('crm.system_agent.title')}
      data-testid="crm-system-agent-panel"
      className="fixed right-0 top-0 z-40 flex h-screen w-full flex-col border-l border-[var(--ant-color-border)] bg-[var(--ant-color-bg-elevated)] shadow-2xl transition-transform duration-200 md:w-[360px]"
      style={{
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-stone-200 px-4 bg-white">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-50 text-amber-700">
            <Sparkles size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="truncate text-sm font-semibold text-stone-900">
                {t('crm.system_agent.title')}
              </p>
              <kbd className="rounded border border-stone-200 bg-stone-100 px-1.5 py-0.2 font-mono text-[9px] text-stone-600">
                ⌘J
              </kbd>
            </div>
            <p className="truncate text-xs text-stone-500">{t('crm.system_agent.subtitle')}</p>
          </div>
        </div>
        <Button
          type="text"
          icon={<X size={16} strokeWidth={1.5} />}
          aria-label={t('crm.system_agent.close')}
          onClick={onClose}
        />
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        {/* Quick launch block */}
        <section className="rounded-2xl border border-amber-200 bg-amber-50/70 dark:bg-amber-950/30 p-4">
          <p className="mb-3 text-[12px] leading-5 text-amber-950">
            {t('crm.system_agent.description')}
          </p>
          <Button
            type="primary"
            block
            icon={<ArrowRight size={15} strokeWidth={1.5} />}
            onClick={() => openChatPage()}
            className="rounded-xl font-medium"
          >
            {t('crm.system_agent.open_chat')}
          </Button>

          {/* Quick Scenario Chips */}
          <div className="mt-3.5 pt-3 border-t border-amber-200/60">
            <p className="text-[11px] font-semibold text-amber-900/80 mb-2">
              {t('crm.ai_chat.slash.title')}:
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {playbooks.map((pb) => (
                <button
                  key={pb.id}
                  type="button"
                  onClick={() => openChatPage()}
                  className="flex items-center gap-1.5 rounded-lg border border-amber-200/80 dark:border-amber-700/60 bg-white/80 px-2 py-1.5 text-[11px] font-medium text-stone-800 hover:border-amber-400 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition cursor-pointer text-left"
                >
                  {pb.icon}
                  <span className="truncate">{pb.label}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="min-h-0">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              {t('crm.system_agent.recent_sessions')}
            </p>
            <EntityStatusChip tone="default" style={{ margin: 0 }}>
              {conversations.conversations.length}
            </EntityStatusChip>
          </div>

          {conversations.isLoading ? (
            <div className="flex min-h-32 items-center justify-center">
              <Spin size="small" />
            </div>
          ) : groups.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('crm.system_agent.empty_sessions')}
            />
          ) : (
            <div className="space-y-4">
              {groups.map((group) => (
                <div key={group.key}>
                  <p className="mb-2 text-[11px] font-semibold text-stone-500">{group.label}</p>
                  <div className="space-y-2">
                    {group.conversations.slice(0, 5).map((conversation) => (
                      <button
                        key={conversation.id}
                        type="button"
                        className="flex w-full items-start gap-2 rounded-xl border border-stone-200 bg-stone-50 p-3 text-left transition hover:border-amber-200 hover:bg-amber-50"
                        onClick={() => openChatPage(conversation.id)}
                      >
                        <MessageSquare
                          size={14}
                          strokeWidth={1.5}
                          className="mt-0.5 shrink-0 text-stone-500"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-[12px] font-semibold text-stone-800">
                            {conversation.title?.trim() ||
                              t('crm.system_agent.session_fallback', {
                                id: conversation.id.slice(0, 8),
                              })}
                          </span>
                          <span className="block truncate text-[11px] text-stone-500">
                            {conversation.contextEntityType === 'none'
                              ? t('crm.system_agent.global_context')
                              : conversation.contextEntityId || conversation.contextEntityType}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </aside>
  );
}
